// src/pages/SkillsMapper.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import skillsData from '../data/skillsdata.json';
import RandomAd from '../components/RandomAd'; // New import for ads

const AdContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 40px 0;
  
  @media (max-width: 768px) {
    margin: 30px 0;
  }
`;

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;

  @media (max-width: 768px) {
    padding: 20px 16px;
  }
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 50px;

  @media (max-width: 768px) {
    margin-bottom: 30px;
  }
`;

const PageTitle = styled.h1`
  font-size: 48px;
  font-weight: 600;
  background: linear-gradient(45deg, #1d1d1f, #424245);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 16px;
  letter-spacing: -0.5px;

  @media (max-width: 768px) {
    font-size: 36px;
  }

  @media (max-width: 480px) {
    font-size: 32px;
  }
`;

const PageSubtitle = styled.p`
  font-size: 21px;
  color: #86868b;
  font-weight: 400;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const TwoColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  margin-top: 40px;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const InputSection = styled.div`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  border-radius: 18px;
  padding: 32px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const ResultsSection = styled.div`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  border-radius: 18px;
  padding: 32px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const SectionTitle = styled.h2`
  font-size: 28px;
  font-weight: 600;
  color: #1d1d1f;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 32px;
`;

const SkillChip = styled.div`
  padding: 12px 20px;
  background: ${props => props.selected ? 
    'linear-gradient(135deg, #0071e3, #0077ed)' : 
    'rgba(255, 255, 255, 0.8)'};
  color: ${props => props.selected ? 'white' : '#1d1d1f'};
  border: 1px solid ${props => props.selected ? 'transparent' : '#d2d2d7'};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: ${props => props.selected ? '500' : '400'};
  text-align: center;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const RoleCard = styled.div`
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid #e8e8ed;
  border-radius: 14px;
  padding: 24px;
  margin-bottom: 20px;
  transition: all 0.3s ease;
  border-left: 4px solid ${props => {
    const score = props.matchScore;
    if (score >= 80) return '#30D158';
    if (score >= 60) return '#FF9F0A';
    return '#FF453A';
  }};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  }
`;

const RoleHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const RoleName = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #1d1d1f;
  margin: 0;
`;

const MatchScore = styled.div`
  background: linear-gradient(135deg, #0071e3, #0077ed);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 16px;
`;

const RoleDescription = styled.p`
  color: #86868b;
  margin-bottom: 16px;
  line-height: 1.5;
`;

const SkillsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SkillTag = styled.span`
  padding: 6px 12px;
  background: ${props => props.matches ? 
    'rgba(0, 113, 227, 0.1)' : 
    'rgba(134, 134, 139, 0.1)'};
  color: ${props => props.matches ? '#0071e3' : '#86868b'};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 400;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #86868b;
  font-size: 18px;
`;

const ClearButton = styled.button`
  padding: 12px 24px;
  background: transparent;
  color: #0071e3;
  border: 1px solid #0071e3;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  margin-top: 20px;

  &:hover {
    background: rgba(0, 113, 227, 0.1);
  }
`;

const SkillsMapper = () => {
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [matchedRoles, setMatchedRoles] = useState([]);
  const [allSkills, setAllSkills] = useState([]);

  useEffect(() => {
    // Extract all unique skills from roles
    const skills = new Set();
    skillsData.roles.forEach(role => {
      role.coreSkills.forEach(skill => skills.add(skill));
    });
    setAllSkills(Array.from(skills));
  }, []);

  useEffect(() => {
    if (selectedSkills.length === 0) {
      setMatchedRoles([]);
      return;
    }

    const matches = skillsData.roles.map(role => {
      const roleSkills = role.coreSkills;
      const matchingSkills = roleSkills.filter(skill => 
        selectedSkills.includes(skill)
      );
      
      const matchScore = Math.round(
        (matchingSkills.length / roleSkills.length) * 100
      );

      return {
        ...role,
        matchScore,
        matchingSkills
      };
    }).filter(role => role.matchScore > 0)
      .sort((a, b) => b.matchScore - a.matchScore);

    setMatchedRoles(matches);
  }, [selectedSkills]);

  const toggleSkill = (skill) => {
    setSelectedSkills(prev => 
      prev.includes(skill) 
        ? prev.filter(s => s !== skill)
        : [...prev, skill]
    );
  };

  const clearSkills = () => {
    setSelectedSkills([]);
  };

  return (
    <PageContainer>
      <HeaderSection>
        <PageTitle>Skills Mapper for Career Starters</PageTitle>
        <PageSubtitle>
          Input your skills to discover which power utility roles are the best fit for your expertise. 
          Select multiple skills to see detailed compatibility scores.
        </PageSubtitle>
      </HeaderSection>
      {/* Strategic Ad Placement 1: Below Header */}
      <AdContainer>
        <RandomAd />
      </AdContainer>
      <TwoColumnLayout>
        <InputSection>
          <SectionTitle>Select Your Skills</SectionTitle>
          <p style={{ color: '#86868b', marginBottom: '24px' }}>
            Choose from {allSkills.length} industry-specific skills
          </p>
          
          <SkillsGrid>
            {allSkills.map((skill, index) => (
              <SkillChip
                key={index}
                selected={selectedSkills.includes(skill)}
                onClick={() => toggleSkill(skill)}
              >
                {skill}
              </SkillChip>
            ))}
          </SkillsGrid>

          {selectedSkills.length > 0 && (
            <ClearButton onClick={clearSkills}>
              Clear {selectedSkills.length} Selected Skills
            </ClearButton>
          )}
        </InputSection>

        <ResultsSection>
          <SectionTitle>
            Recommended Roles 
            {matchedRoles.length > 0 && ` (${matchedRoles.length})`}
          </SectionTitle>
          {/* Strategic Ad Placement 2: Within Results Section */}
          {matchedRoles.length > 0 && (
            <AdContainer>
              <RandomAd />
            </AdContainer>
          )}

          {matchedRoles.length > 0 ? (
            matchedRoles.map((role, index) => (
              <RoleCard key={index} matchScore={role.matchScore}>
                <RoleHeader>
                  <RoleName>{role.title}</RoleName>
                  <MatchScore>{role.matchScore}% Match</MatchScore>
                </RoleHeader>
                <RoleDescription>{role.description}</RoleDescription>
                <SkillsList>
                  {role.coreSkills.map((skill, skillIndex) => (
                    <SkillTag 
                      key={skillIndex}
                      matches={role.matchingSkills.includes(skill)}
                    >
                      {skill}
                    </SkillTag>
                  ))}
                </SkillsList>
              </RoleCard>
            ))
          ) : (
            <EmptyState>
              {selectedSkills.length === 0 
                ? 'Select skills above to see role recommendations'
                : 'No roles match your selected skills. Try selecting different skills.'
              }
            </EmptyState>
          )}
        </ResultsSection>
      </TwoColumnLayout>
    </PageContainer>
  );
};

export default SkillsMapper;